import React from 'react';
import Icon from '../shared/Icon';
import RowActionsMenu from '../shared/RowActionsMenu';
import './Library.css'; // library-icon-trigger, library-menu

/**
 * A document's three-dot menu (PRD-1606, PRD-1607): the Bridge Icon trigger,
 * the same on a card and a list row, so the same document never reads as two
 * controls. Built on the shared RowActionsMenu as a disclosure: focus stays on
 * the trigger, Escape hands it back, and the menu opens gap-2 below,
 * right-aligned, flipping above when there is no room. The 20px glyph is
 * Bold, a recorded exception to the icon size and weight rule (PRD-1630).
 *
 * The actions follow the upload state: Uploading has none (no trigger),
 * Available can be archived, Failed can be retried or archived, and an
 * archived document can only be unarchived. An archived trigger stays at full
 * opacity, because the way out is through it, and is described by the item's
 * "Archived" tooltip.
 *
 * `actions` holds the page's handlers: onRetry, onArchive, onUnarchive, each
 * called with the document.
 */
export const archivedTipId = (doc) => `library-archived-${doc.id}`;

/**
 * Dimming alone doesn't say why, so an archived item carries this tooltip
 * (Bridge 1163:4474), using the note slot that carries a locked nav item's
 * reason. It's one sentence, not a flex row, so the space before the note stays.
 */
export function ArchivedTip({ doc }) {
  if (!doc.archived) return null;
  return (
    <span className="library-tip" id={archivedTipId(doc)} role="tooltip">
      Archived<span className="library-tip-note"> · Unarchive to use it</span>
    </span>
  );
}

function LibraryDocumentMenu({ doc, actions: handlers }) {
  const actions = [];
  if (doc.archived) {
    actions.push({ key: 'unarchive', label: 'Unarchive', run: handlers.onUnarchive });
  } else if (doc.status !== 'uploading') {
    if (doc.status === 'failed') actions.push({ key: 'retry', label: 'Retry', icon: 'arrows-clockwise', run: handlers.onRetry });
    actions.push({ key: 'archive', label: 'Archive', danger: true, run: handlers.onArchive });
  }
  if (!actions.length) return null;

  return (
    <div className="library-menu-anchor" data-doc-menu={doc.id}>
      <RowActionsMenu
        ariaLabel={`Actions for ${doc.name}`}
        ariaDescribedBy={doc.archived ? archivedTipId(doc) : undefined}
        triggerClassName="library-icon-trigger"
        menuClassName="library-menu"
        iconSize={20}
        iconWeight="bold"
        gap={8}
        edge={16}
        disclosure
      >
        {(close) => actions.map((action) => (
          <button
            key={action.key}
            type="button"
            className={action.danger ? 'row-actions-menu-danger' : undefined}
            onClick={() => {
              close();
              action.run(doc);
            }}
          >
            <Icon name={action.icon || 'archive'} size={20} />
            <span>{action.label}</span>
          </button>
        ))}
      </RowActionsMenu>
    </div>
  );
}

export default LibraryDocumentMenu;
